import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, CardActionArea,
  Container, Chip, Avatar, Button, useTheme, alpha, Paper,
  IconButton, Divider, Tabs, Tab, CircularProgress, LinearProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  MenuOpen as MenuOpenIcon,
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
  ArrowForward as ArrowForwardIcon,
  Business as BusinessIcon,
  Inventory as InventoryIcon,
  Science as ScienceIcon,
  // Import all the necessary icons
  Medication as PillIcon,
  Flight as PlaneIcon,
  DirectionsCar as CarIcon,
  Storage as ServerIcon,
  Explore as TourIcon,
  Apartment as BuildingIcon,
  AttachMoney as CurrencyIcon,
  LocalGasStation as OilIcon,
  Terrain as MiningIcon,
  Restaurant as FoodIcon,
  WbSunny as SunIcon,
  Favorite as HeartIcon,
  LocalShipping as TruckIcon,
  Person as PersonIcon,
  ShowChart as ChartIcon,
  Public as GlobalIcon,
  Insights as InsightsIcon,
  AccountBalance as BankIcon,
  AutoGraph as GraphIcon,
  Diversity3 as NetworkIcon,
  Category as CategoryIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import * as industryApi from '../services/industry';
import * as userApi from '../services/user';
import * as articleApi from '../services/article';
import * as homeContentApi from '../services/homeContent';
import * as marketplaceApi from '../services/marketplace';
import * as heroApi from '../services/hero';
import Sidebar from '../components/common/Sidebar';

const MotionCard = motion(Card);

const drawerWidth = 240;

const MainContent = styled(Box)(({ theme, isSidebarOpen }) => ({
  flexGrow: 1,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: isSidebarOpen ? drawerWidth : 0,
  width: '100%'
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -8,
    left: 0,
    width: 60,
    height: 4,
    borderRadius: 2,
    backgroundColor: theme.palette.primary.main,
  }
}));

const CategoryContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(6),
  position: 'relative',
}));

const Home = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });

  const y1 = useTransform(scrollYProgress, [0, 1], [0, 300]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const y3 = useTransform(scrollYProgress, [0, 1], [0, 100]);
  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [1, 0.8, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.2]);

  const [activeTab, setActiveTab] = useState(0);
  const [marketplaceTab, setMarketplaceTab] = useState(0);
  const [industries, setIndustries] = useState([]);
  const [members, setMembers] = useState([]);
  const [newsArticles, setNewsArticles] = useState([]);
  const [innovationArticles, setInnovationArticles] = useState([]);
  const [featuredIndustries, setFeaturedIndustries] = useState([]);
  const [featuredExperts, setFeaturedExperts] = useState([]);
  const [featuredTestimonials, setFeaturedTestimonials] = useState([]);
  const [investmentOpportunities, setInvestmentOpportunities] = useState([]);
  const [investorRequests, setInvestorRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroData, setHeroData] = useState({
    title: 'Value Chain Global',
    subtitle: 'MAKE BETTER INVESTMENT DECISIONS',
    description: 'The go-to platform to discover high-growth Saudi sectors, identify where innovation fits, showcase your offerings, and connect with key industry players. Find your best rewarding place in the Saudi Economic Sectors.',
    buttonText: 'Get Started',
    buttonLink: '/industries'
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [
          industriesData,
          membersData,
          articlesData,
          innovationsData,
          heroSectionData,
          homeContentData,
          projectsData,
          requestsData
        ] = await Promise.all([
          industryApi.getAllIndustries({ includeSubIndustries: true }),
          userApi.getMembers(),
          articleApi.getArticles(),
          articleApi.getInnovations(),
          heroApi.getHeroSection(),
          homeContentApi.getHomeContent(),
          marketplaceApi.getAllProjects(),
          marketplaceApi.getAllInvestorRequests()
        ]);

        setIndustries(industriesData);
        setMembers(membersData);
        setNewsArticles(articlesData);
        setInnovationArticles(innovationsData);

        // Set hero section data
        if (heroSectionData) {
          setHeroData(heroSectionData);
        }

        // Set featured content from home content API
        if (homeContentData.content.featured_industries) {
          setFeaturedIndustries(homeContentData.content.featured_industries.map(item => item.resource));
        }
        if (homeContentData.content.featured_experts) {
          // Transform expert data to match UI expectations
          setFeaturedExperts(homeContentData.content.featured_experts.map(item => ({
            id: item.resource.id,
            name: `${item.resource.firstName} ${item.resource.lastName}`,
            title: item.resource.title || '',
            organization: item.resource.organizationName || '',
            photo: item.resource.photoUrl || '',
            tag: 'Expert',
            sectors: item.resource.selectedSectors || [],
            expertProfile: item.resource.expertProfile
          })));
        }
        if (homeContentData.content.featured_testimonials) {
          setFeaturedTestimonials(homeContentData.content.featured_testimonials.map(item => item.resource));
        }

        // Set marketplace data
        setInvestmentOpportunities(projectsData);
        setInvestorRequests(requestsData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleIndustryClick = (industryId) => {
    navigate(`/industry/${industryId}`);
  };

  // Handler for navigating to article page
  const handleArticleClick = (articleType, articleId) => {
    navigate(`/article/${articleType}/${articleId}`);
  };

  // Handler for the "View All" button
  const handleViewAllArticles = (articleType) => {
    navigate(`/articles/`);
  };

  const handleMarketplaceTabChange = (event, newValue) => {
    setMarketplaceTab(newValue);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getAvatarColor = (industryName) => {
    const colors = {
      'Pharmaceutical': '#5C6BC0',
      'Aerospace': '#26A69A',
      'Automotive': '#EF5350',
      'Medical Devices': '#66BB6A',
      'ICT': '#42A5F5',
      'Finance': '#AB47BC',
      'Tourism': '#FF7043',
      'Petroleum': '#8D6E63',
      'Mining': '#5D4037',
      'default': '#FFA726',
    };
    return colors[industryName] || colors.default;
  };

  // Function to map icon string to actual Icon component
  const getIndustryIcon = (iconName) => {
    const iconMap = {
      'PillIcon': <PillIcon />,
      'PlaneIcon': <PlaneIcon />,
      'CarIcon': <CarIcon />,
      'ServerIcon': <ServerIcon />,
      'TourIcon': <TourIcon />,
      'BuildingIcon': <BuildingIcon />,
      'CurrencyIcon': <CurrencyIcon />,
      'OilIcon': <OilIcon />,
      'MiningIcon': <MiningIcon />,
      'FoodIcon': <FoodIcon />,
      'SunIcon': <SunIcon />,
      'HeartIcon': <HeartIcon />,
      'TruckIcon': <TruckIcon />
    };

    return iconMap[iconName] || <BusinessIcon />;  // Default to BusinessIcon if not found
  };

  const getTabIcon = (index) => {
    switch (index) {
      case 0: return <BusinessIcon />;
      case 1: return <InventoryIcon />;
      case 2: return <ScienceIcon />;
      default: return null;
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      

      <MainContent isSidebarOpen={false}>
        

        <Container maxWidth="xl" sx={{ pt: 4, pb: 6 }}>
          {/* 3D ANIMATED HERO SECTION */}
          <Box
            ref={heroRef}
            sx={{
              mb: 4,
              borderRadius: 4,
              overflow: 'hidden',
              position: 'relative',
              background: `linear-gradient(135deg, #0A1929 0%, ${theme.palette.primary.dark} 50%, ${theme.palette.secondary.dark} 100%)`,
              minHeight: { xs: '500px', md: '580px' },
              perspective: '1000px',
              transformStyle: 'preserve-3d'
            }}
          >
            {/* Animated Grid Background */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: `
                  linear-gradient(90deg, ${alpha(theme.palette.primary.light, 0.05)} 1px, transparent 1px),
                  linear-gradient(${alpha(theme.palette.primary.light, 0.05)} 1px, transparent 1px)
                `,
                backgroundSize: '50px 50px',
                animation: 'gridMove 20s linear infinite',
                transform: 'rotateX(60deg) translateZ(-100px)'
              }}
            />

            {/* Gradient Orbs for Depth */}
            <Box
              sx={{
                position: 'absolute',
                top: '10%',
                left: '15%',
                width: '400px',
                height: '400px',
                borderRadius: '50%',
                background: `radial-gradient(circle, ${alpha('#4FC3F7', 0.15)}, transparent 70%)`,
                filter: 'blur(60px)',
                animation: 'float1 20s ease-in-out infinite'
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: '15%',
                right: '10%',
                width: '350px',
                height: '350px',
                borderRadius: '50%',
                background: `radial-gradient(circle, ${alpha('#BA68C8', 0.15)}, transparent 70%)`,
                filter: 'blur(60px)',
                animation: 'float2 18s ease-in-out infinite'
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '500px',
                height: '500px',
                borderRadius: '50%',
                background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.1)}, transparent 70%)`,
                filter: 'blur(80px)',
                animation: 'float3 25s ease-in-out infinite'
              }}
            />


            {/* Floating 3D Cubes with Industry Icons */}
            {[
              { Icon: GraphIcon, color: '#4FC3F7', label: 'Analytics' },
              { Icon: NetworkIcon, color: '#BA68C8', label: 'Network' },
              { Icon: BusinessIcon, color: '#FF8A65', label: 'Business' },
              { Icon: CategoryIcon, color: '#FFD54F', label: 'Industries' },
              { Icon: InsightsIcon, color: '#81C784', label: 'Insights' },
              { Icon: GlobalIcon, color: '#64B5F6', label: 'Global' },
              { Icon: BankIcon, color: '#E57373', label: 'Finance' },
              { Icon: ChartIcon, color: '#9575CD', label: 'Growth' },
              { Icon: PillIcon, color: '#4DD0E1', label: 'Healthcare' },
              { Icon: PlaneIcon, color: '#FFB74D', label: 'Aerospace' },
              { Icon: CarIcon, color: '#F06292', label: 'Automotive' },
              { Icon: ServerIcon, color: '#7986CB', label: 'Technology' },
              { Icon: TourIcon, color: '#FFD54F', label: 'Tourism' },
              { Icon: OilIcon, color: '#A1887F', label: 'Energy' },
              { Icon: FoodIcon, color: '#AED581', label: 'Food' }
            ].map(({ Icon, color, label }, i) => {
              const size = 70 + (i % 3) * 20;
              const x = 5 + (i * 6.5) % 90;
              const y = 10 + ((i * 17) % 80);
              const duration = 20 + (i % 5) * 5;
              const delay = (i % 8) * 1.5;

              return (
                <motion.div
                  key={`cube-${i}`}
                  initial={{
                    opacity: 0,
                    rotateX: 0,
                    rotateY: 0,
                    rotateZ: 0,
                    z: -200
                  }}
                  animate={{
                    opacity: [0, 0.9, 0],
                    rotateX: [0, 360],
                    rotateY: [0, 360],
                    rotateZ: [0, 180],
                    z: [-200, 200, -200],
                    x: [-30, 30, -30],
                    y: [-20, 20, -20]
                  }}
                  transition={{
                    duration,
                    delay,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                  style={{
                    position: 'absolute',
                    left: `${x}%`,
                    top: `${y}%`,
                    width: size,
                    height: size,
                    transformStyle: 'preserve-3d'
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      background: `linear-gradient(135deg, ${alpha(color, 0.25)}, ${alpha(color, 0.08)})`,
                      border: `2px solid ${alpha(color, 0.4)}`,
                      boxShadow: `
                        0 0 40px ${alpha(color, 0.5)},
                        inset 0 0 20px ${alpha(color, 0.1)}
                      `,
                      borderRadius: '12px',
                      backdropFilter: 'blur(15px)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                      p: 1.5,
                      transformStyle: 'preserve-3d'
                    }}
                  >
                    <Icon
                      sx={{
                        fontSize: size * 0.45,
                        color: color,
                        filter: `drop-shadow(0 0 12px ${color})`,
                        mb: 0.5
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: size * 0.14,
                        fontWeight: 700,
                        color: alpha(color, 0.95),
                        textShadow: `0 0 8px ${alpha(color, 0.6)}`,
                        textAlign: 'center',
                        lineHeight: 1
                      }}
                    >
                      {label}
                    </Typography>
                  </Box>
                </motion.div>
              );
            })}


            {/* Main Content with Parallax */}
            <motion.div style={{ y: y3, opacity, scale }}>
              <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 10, py: { xs: 6, md: 8 } }}>
                <Box sx={{ textAlign: 'center' }}>
                  {/* Animated Badge with 3D effect */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0, rotateX: -90 }}
                    animate={{
                      opacity: 1,
                      scale: 1,
                      rotateX: 0,
                      rotateY: [0, 5, 0, -5, 0]
                    }}
                    transition={{
                      duration: 0.8,
                      type: 'spring',
                      bounce: 0.5,
                      rotateY: {
                        duration: 4,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }
                    }}
                    style={{ perspective: '1000px' }}
                  >
                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                      {/* Badge glow effect */}
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: -8,
                          background: `radial-gradient(circle, ${alpha('#00E5FF', 0.4)}, transparent 70%)`,
                          borderRadius: 10,
                          filter: 'blur(20px)',
                          animation: 'badgeGlow 3s ease-in-out infinite'
                        }}
                      />
                      <Chip
                        label={heroData.subtitle}
                        icon={<TrendingUpIcon />}
                        sx={{
                          position: 'relative',
                          bgcolor: alpha('#00E5FF', 0.15),
                          color: '#00E5FF',
                          fontWeight: 800,
                          fontSize: '1rem',
                          px: 4,
                          py: 3,
                          height: 'auto',
                          mb: 5,
                          backdropFilter: 'blur(30px)',
                          border: `2px solid ${alpha('#00E5FF', 0.5)}`,
                          boxShadow: `0 0 40px ${alpha('#00E5FF', 0.4)}, inset 0 0 20px ${alpha('#00E5FF', 0.1)}`,
                          '& .MuiChip-icon': {
                            color: '#00E5FF',
                            fontSize: '1.3rem',
                            filter: 'drop-shadow(0 0 8px #00E5FF)',
                            animation: 'iconSpin 4s linear infinite'
                          },
                          animation: 'badgePulse 3s ease-in-out infinite'
                        }}
                      />
                    </Box>
                  </motion.div>

                  {/* Main Title with Gradient & Glow */}
                  <motion.div
                    initial={{ opacity: 0, y: 80, rotateX: 90 }}
                    animate={{ opacity: 1, y: 0, rotateX: 0 }}
                    transition={{ duration: 1, delay: 0.2, type: 'spring' }}
                  >
                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                      {/* Title ambient glow */}
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: -20,
                          background: `radial-gradient(ellipse, ${alpha(theme.palette.primary.main, 0.3)}, transparent 70%)`,
                          filter: 'blur(40px)',
                          animation: 'titleAmbient 5s ease-in-out infinite',
                          zIndex: 0
                        }}
                      />
                      <Typography
                        variant="h1"
                        component="h1"
                        sx={{
                          position: 'relative',
                          background: `linear-gradient(135deg, #fff 0%, ${theme.palette.primary.light} 50%, ${theme.palette.secondary.light} 100%)`,
                          backgroundSize: '200% 200%',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          fontWeight: 900,
                          fontSize: { xs: '2.75rem', sm: '3.75rem', md: '5rem' },
                          lineHeight: 1.1,
                          mb: 3,
                          filter: `drop-shadow(0 0 30px ${alpha(theme.palette.primary.main, 0.6)})`,
                          letterSpacing: '-0.03em',
                          animation: 'titleGradient 8s ease-in-out infinite, titleGlow 4s ease-in-out infinite',
                          zIndex: 1
                        }}
                      >
                        {heroData.title}
                      </Typography>
                    </Box>
                  </motion.div>

                  {/* Description with Typewriter Effect */}
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        color: alpha('#fff', 0.9),
                        fontWeight: 400,
                        lineHeight: 1.7,
                        mb: 5,
                        maxWidth: '800px',
                        mx: 'auto',
                        fontSize: { xs: '1.05rem', md: '1.2rem' },
                        textShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.5)}`,
                        backdropFilter: 'blur(2px)'
                      }}
                    >
                      {heroData.description}
                    </Typography>
                  </motion.div>

                  {/* 3D CTA Button */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0, rotateY: 180 }}
                    animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                    transition={{ duration: 0.8, delay: 0.8, type: 'spring', bounce: 0.5 }}
                    whileHover={{ scale: 1.1, rotateY: 10 }}
                    style={{ perspective: '1000px' }}
                  >
                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                      {/* Multi-layer glow effect */}
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: -15,
                          background: `radial-gradient(circle, ${alpha('#00E5FF', 0.6)}, transparent 70%)`,
                          borderRadius: 5,
                          filter: 'blur(25px)',
                          animation: 'buttonMegaGlow 2s ease-in-out infinite'
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: -8,
                          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.4)}, ${alpha(theme.palette.secondary.main, 0.4)})`,
                          borderRadius: 4,
                          filter: 'blur(15px)',
                          animation: 'buttonMegaGlow 2s ease-in-out infinite reverse'
                        }}
                      />
                      <Button
                        variant="contained"
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => navigate(heroData.buttonLink || '/industries')}
                        sx={{
                          position: 'relative',
                          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                          color: 'white',
                          px: 7,
                          py: 2.5,
                          fontSize: '1.25rem',
                          fontWeight: 800,
                          borderRadius: 3.5,
                          textTransform: 'none',
                          border: `2px solid ${alpha('#00E5FF', 0.5)}`,
                          boxShadow: `
                            0 0 40px ${alpha(theme.palette.primary.main, 0.8)},
                            inset 0 2px 10px ${alpha('#fff', 0.2)},
                            inset 0 -2px 10px ${alpha('#000', 0.2)}
                          `,
                          '&:hover': {
                            background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.secondary.light})`,
                            transform: 'translateY(-8px) rotateX(10deg)',
                            boxShadow: `
                              0 20px 80px ${alpha(theme.palette.primary.main, 0.9)},
                              0 0 60px ${alpha('#00E5FF', 0.8)},
                              inset 0 2px 15px ${alpha('#fff', 0.3)}
                            `
                          },
                          transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                          '& .MuiButton-endIcon': {
                            animation: 'arrowFloat 2s ease-in-out infinite'
                          }
                        }}
                      >
                        {heroData.buttonText || 'Explore Industries'}
                      </Button>
                    </Box>
                  </motion.div>
                </Box>
              </Container>
            </motion.div>
          </Box>

          {/* Advanced CSS Animations */}
          <style>
            {`
              @keyframes gridMove {
                0% { transform: rotateX(60deg) translateZ(-100px) translateY(0); }
                100% { transform: rotateX(60deg) translateZ(-100px) translateY(50px); }
              }
              @keyframes iconFloat {
                0%, 100% { transform: translateY(0px) rotateZ(0deg); }
                50% { transform: translateY(-15px) rotateZ(5deg); }
              }
              @keyframes float1 {
                0%, 100% { transform: translate(0, 0); }
                33% { transform: translate(30px, -30px); }
                66% { transform: translate(-30px, 20px); }
              }
              @keyframes float2 {
                0%, 100% { transform: translate(0, 0); }
                33% { transform: translate(-25px, 25px); }
                66% { transform: translate(25px, -20px); }
              }
              @keyframes float3 {
                0%, 100% { transform: translate(-50%, -50%) scale(1); }
                50% { transform: translate(-50%, -50%) scale(1.1); }
              }
              @keyframes badgeGlow {
                0%, 100% { opacity: 0.4; transform: scale(1); }
                50% { opacity: 0.7; transform: scale(1.2); }
              }
              @keyframes badgePulse {
                0%, 100% { box-shadow: 0 0 40px ${alpha('#00E5FF', 0.4)}, inset 0 0 20px ${alpha('#00E5FF', 0.1)}; }
                50% { box-shadow: 0 0 60px ${alpha('#00E5FF', 0.6)}, inset 0 0 30px ${alpha('#00E5FF', 0.2)}; }
              }
              @keyframes iconSpin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
              @keyframes titleAmbient {
                0%, 100% { opacity: 0.3; transform: scale(1); }
                50% { opacity: 0.5; transform: scale(1.1); }
              }
              @keyframes titleGradient {
                0% { background-position: 0% 50%; }
                50% { background-position: 100% 50%; }
                100% { background-position: 0% 50%; }
              }
              @keyframes titleGlow {
                0%, 100% { filter: drop-shadow(0 0 30px ${alpha(theme.palette.primary.main, 0.6)}); }
                50% { filter: drop-shadow(0 0 50px ${alpha(theme.palette.primary.main, 0.9)}); }
              }
              @keyframes buttonMegaGlow {
                0%, 100% { opacity: 0.6; transform: scale(1); }
                50% { opacity: 1; transform: scale(1.15); }
              }
              @keyframes arrowFloat {
                0%, 100% { transform: translateX(0); }
                50% { transform: translateX(5px); }
              }
            `}
          </style>

          {/* Industry Categories Tabs - Enhanced */}
          <Box sx={{ position: 'relative', mb: 6 }}>
            {/* Section Header with Animation */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Box sx={{ textAlign: 'center', mb: 5 }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    mb: 2,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  Explore Saudi Industries
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '700px', mx: 'auto' }}>
                  Discover innovation and investment opportunities across strategic sectors
                </Typography>
              </Box>
            </motion.div>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                overflow: 'hidden',
                background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)}, ${alpha(theme.palette.background.paper, 0.95)})`,
                backdropFilter: 'blur(20px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`
              }}
            >
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                textColor="primary"
                indicatorColor="primary"
                variant="fullWidth"
                sx={{
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.secondary.main, 0.03)})`,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  '& .MuiTab-root': {
                    minHeight: 80,
                    px: 4,
                    py: 2,
                    fontWeight: 600,
                    fontSize: '1.05rem',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    '&:hover': {
                      background: alpha(theme.palette.primary.main, 0.05),
                      transform: 'translateY(-2px)'
                    },
                    '&.Mui-selected': {
                      background: alpha(theme.palette.primary.main, 0.08),
                      color: theme.palette.primary.main,
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.4)}`
                      }
                    }
                  },
                  '& .MuiTabs-indicator': {
                    height: 0
                  },
                  '& .MuiTab-iconWrapper': {
                    fontSize: '1.8rem',
                    mb: 0.5
                  }
                }}
              >
                <Tab
                  label="New Manufacturing"
                  icon={getTabIcon(0)}
                  iconPosition="top"
                />
                <Tab
                  label="Service & Infrastructure"
                  icon={getTabIcon(1)}
                  iconPosition="top"
                />
                <Tab
                  label="Mature Industries"
                  icon={getTabIcon(2)}
                  iconPosition="top"
                />
              </Tabs>

            <Box sx={{ p: { xs: 3, md: 5 } }}>
              <AnimatePresence mode="wait">
                {activeTab === 0 && (
                  <motion.div
                    key="tab-0"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.4 }}
                  >
                    <Box sx={{ mb: 5 }}>
                      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                        New Saudi Manufacturing Industries
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', maxWidth: '800px' }}>
                        Industries prioritized for new manufacturing, localization, technology transfer, and R&D establishment in Saudi Arabia.
                      </Typography>
                    </Box>

                    <Grid container spacing={3}>
                      {industries
                        .filter(ind => ind.category === 'Localization')
                        .map((industry, index) => (
                          <Grid item xs={12} sm={6} md={4} lg={3} key={industry.id}>
                            <motion.div
                              initial={{ opacity: 0, y: 30 }}
                              whileInView={{ opacity: 1, y: 0 }}
                              viewport={{ once: true }}
                              transition={{ duration: 0.4, delay: index * 0.05 }}
                            >
                              <MotionCard
                                whileHover={{ y: -12, scale: 1.02 }}
                                elevation={0}
                                sx={{
                                  borderRadius: 3,
                                  height: '100%',
                                  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  position: 'relative',
                                  overflow: 'hidden',
                                  '&:hover': {
                                    boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                    '&::before': {
                                      opacity: 1
                                    }
                                  },
                                  '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: '100%',
                                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, transparent)`,
                                    opacity: 0,
                                    transition: 'opacity 0.3s ease'
                                  }
                                }}
                              >
                                <CardActionArea
                                  onClick={() => handleIndustryClick(industry.id)}
                                  sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    position: 'relative',
                                    zIndex: 1
                                  }}
                                >
                                  {/* Top Gradient Strip */}
                                  <Box
                                    sx={{
                                      height: 6,
                                      width: '100%',
                                      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                                    }}
                                  />

                                  <CardContent sx={{ p: 3.5, width: '100%' }}>
                                    {/* Icon with Glow */}
                                    <Box sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
                                      <Box
                                        sx={{
                                          position: 'absolute',
                                          inset: -8,
                                          background: `radial-gradient(circle, ${alpha(getAvatarColor(industry.name), 0.3)}, transparent 70%)`,
                                          filter: 'blur(12px)',
                                          opacity: 0.6
                                        }}
                                      />
                                      <Avatar
                                        sx={{
                                          bgcolor: getAvatarColor(industry.name),
                                          width: 64,
                                          height: 64,
                                          boxShadow: `0 8px 16px ${alpha(getAvatarColor(industry.name), 0.3)}`,
                                          position: 'relative'
                                        }}
                                      >
                                        {getIndustryIcon(industry.icon)}
                                      </Avatar>
                                    </Box>

                                    <Typography
                                      variant="h6"
                                      sx={{
                                        mb: 1.5,
                                        fontWeight: 700,
                                        color: theme.palette.text.primary,
                                        lineHeight: 1.3
                                      }}
                                    >
                                      {industry.name}
                                    </Typography>

                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{
                                        mb: 2.5,
                                        lineHeight: 1.6,
                                        minHeight: '3em'
                                      }}
                                    >
                                      {industry.shortDescription || 'Explore investment opportunities and market insights'}
                                    </Typography>

                                    {industry.hasSubIndustries && (
                                      <Chip
                                        label={`${industry.subIndustries?.length || 0} Sub-Industries`}
                                        size="small"
                                        sx={{
                                          mb: 2,
                                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                                          color: theme.palette.primary.main,
                                          fontWeight: 600,
                                          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                                        }}
                                      />
                                    )}

                                    <Box
                                      sx={{
                                        mt: 'auto',
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: theme.palette.primary.main,
                                        fontWeight: 600
                                      }}
                                    >
                                      <Typography variant="button" sx={{ mr: 0.5 }}>
                                        Explore
                                      </Typography>
                                      <motion.div
                                        animate={{ x: [0, 4, 0] }}
                                        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                                      >
                                        <ArrowForwardIcon fontSize="small" />
                                      </motion.div>
                                    </Box>
                                  </CardContent>
                                </CardActionArea>
                              </MotionCard>
                            </motion.div>
                          </Grid>
                        ))}
                    </Grid>
                  </motion.div>
                )}
              </AnimatePresence>

              {activeTab === 1 && (
                <motion.div
                  key="tab-1"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4 }}
                >
                  <Box sx={{ mb: 5 }}>
                    <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                      Saudi Service & Infrastructure Sectors
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', maxWidth: '800px' }}>
                      Service, platform, and infrastructure sectors that enable economic growth and offer strong opportunities for innovation, investment, and market entry in Saudi Arabia.
                    </Typography>
                  </Box>

                  <Grid container spacing={3}>
                    {industries
                      .filter(ind => ind.category === 'Service & Infrastructure')
                      .map((industry, index) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={industry.id}>
                          <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: index * 0.05 }}
                          >
                            <MotionCard
                              whileHover={{ y: -12, scale: 1.02 }}
                              elevation={0}
                              sx={{
                                borderRadius: 3,
                                height: '100%',
                                background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.secondary.main, 0.02)})`,
                                border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                position: 'relative',
                                overflow: 'hidden',
                                '&:hover': {
                                  boxShadow: `0 20px 40px ${alpha(theme.palette.secondary.main, 0.15)}`,
                                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                                  '&::before': {
                                    opacity: 1
                                  }
                                },
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  height: '100%',
                                  background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.05)}, transparent)`,
                                  opacity: 0,
                                  transition: 'opacity 0.3s ease'
                                }
                              }}
                            >
                              <CardActionArea
                                onClick={() => handleIndustryClick(industry.id)}
                                sx={{
                                  height: '100%',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  position: 'relative',
                                  zIndex: 1
                                }}
                              >
                                <Box
                                  sx={{
                                    height: 6,
                                    width: '100%',
                                    background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.light})`,
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.secondary.main, 0.3)}`
                                  }}
                                />

                                <CardContent sx={{ p: 3.5, width: '100%' }}>
                                  <Box sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        inset: -8,
                                        background: `radial-gradient(circle, ${alpha(getAvatarColor(industry.name), 0.3)}, transparent 70%)`,
                                        filter: 'blur(12px)',
                                        opacity: 0.6
                                      }}
                                    />
                                    <Avatar
                                      sx={{
                                        bgcolor: getAvatarColor(industry.name),
                                        width: 64,
                                        height: 64,
                                        boxShadow: `0 8px 16px ${alpha(getAvatarColor(industry.name), 0.3)}`,
                                        position: 'relative'
                                      }}
                                    >
                                      {getIndustryIcon(industry.icon)}
                                    </Avatar>
                                  </Box>

                                  <Typography
                                    variant="h6"
                                    sx={{
                                      mb: 1.5,
                                      fontWeight: 700,
                                      color: theme.palette.text.primary,
                                      lineHeight: 1.3
                                    }}
                                  >
                                    {industry.name}
                                  </Typography>

                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      mb: 2.5,
                                      lineHeight: 1.6,
                                      minHeight: '3em'
                                    }}
                                  >
                                    {industry.shortDescription || 'Explore service sector opportunities and market insights'}
                                  </Typography>

                                  <Box
                                    sx={{
                                      mt: 'auto',
                                      display: 'flex',
                                      alignItems: 'center',
                                      color: theme.palette.secondary.main,
                                      fontWeight: 600
                                    }}
                                  >
                                    <Typography variant="button" sx={{ mr: 0.5 }}>
                                      Explore
                                    </Typography>
                                    <motion.div
                                      animate={{ x: [0, 4, 0] }}
                                      transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                                    >
                                      <ArrowForwardIcon fontSize="small" />
                                    </motion.div>
                                  </Box>
                                </CardContent>
                              </CardActionArea>
                            </MotionCard>
                          </motion.div>
                        </Grid>
                      ))}
                  </Grid>
                </motion.div>
              )}

              {activeTab === 2 && (
                <motion.div
                  key="tab-2"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4 }}
                >
                  <Box sx={{ mb: 5 }}>
                    <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                      Saudi Mature Industries
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', maxWidth: '800px' }}>
                      Well-established Saudi industries with proven economic contribution, developed value chains, and ongoing opportunities for expansion, efficiency, specialization, and export growth.
                    </Typography>
                  </Box>

                  <Grid container spacing={3}>
                    {industries
                      .filter(ind => ind.category === 'Mature Industries')
                      .map((industry, index) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={industry.id}>
                          <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: index * 0.05 }}
                          >
                            <MotionCard
                              whileHover={{ y: -12, scale: 1.02 }}
                              elevation={0}
                              sx={{
                                borderRadius: 3,
                                height: '100%',
                                background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.info.main, 0.02)})`,
                                border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                position: 'relative',
                                overflow: 'hidden',
                                '&:hover': {
                                  boxShadow: `0 20px 40px ${alpha(theme.palette.info.main, 0.15)}`,
                                  border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                                  '&::before': {
                                    opacity: 1
                                  }
                                },
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  height: '100%',
                                  background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.05)}, transparent)`,
                                  opacity: 0,
                                  transition: 'opacity 0.3s ease'
                                }
                              }}
                            >
                              <CardActionArea
                                onClick={() => handleIndustryClick(industry.id)}
                                sx={{
                                  height: '100%',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  position: 'relative',
                                  zIndex: 1
                                }}
                              >
                                <Box
                                  sx={{
                                    height: 6,
                                    width: '100%',
                                    background: `linear-gradient(90deg, ${theme.palette.info.main}, ${theme.palette.info.light})`,
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.info.main, 0.3)}`
                                  }}
                                />

                                <CardContent sx={{ p: 3.5, width: '100%' }}>
                                  <Box sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        inset: -8,
                                        background: `radial-gradient(circle, ${alpha(getAvatarColor(industry.name), 0.3)}, transparent 70%)`,
                                        filter: 'blur(12px)',
                                        opacity: 0.6
                                      }}
                                    />
                                    <Avatar
                                      sx={{
                                        bgcolor: getAvatarColor(industry.name),
                                        width: 64,
                                        height: 64,
                                        boxShadow: `0 8px 16px ${alpha(getAvatarColor(industry.name), 0.3)}`,
                                        position: 'relative'
                                      }}
                                    >
                                      {getIndustryIcon(industry.icon)}
                                    </Avatar>
                                  </Box>

                                  <Typography
                                    variant="h6"
                                    sx={{
                                      mb: 1.5,
                                      fontWeight: 700,
                                      color: theme.palette.text.primary,
                                      lineHeight: 1.3
                                    }}
                                  >
                                    {industry.name}
                                  </Typography>

                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      mb: 2.5,
                                      lineHeight: 1.6,
                                      minHeight: '3em'
                                    }}
                                  >
                                    {industry.shortDescription || 'Explore established industry sectors and market insights'}
                                  </Typography>

                                  <Box
                                    sx={{
                                      mt: 'auto',
                                      display: 'flex',
                                      alignItems: 'center',
                                      color: theme.palette.info.main,
                                      fontWeight: 600
                                    }}
                                  >
                                    <Typography variant="button" sx={{ mr: 0.5 }}>
                                      Explore
                                    </Typography>
                                    <motion.div
                                      animate={{ x: [0, 4, 0] }}
                                      transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                                    >
                                      <ArrowForwardIcon fontSize="small" />
                                    </motion.div>
                                  </Box>
                                </CardContent>
                              </CardActionArea>
                            </MotionCard>
                          </motion.div>
                        </Grid>
                      ))}
                  </Grid>
                </motion.div>
              )}
            </Box>
          </Paper>
          </Box>

          {/* Featured Industries Section */}
          <Box sx={{ mb: 8 }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    mb: 2,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  Featured Industries
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '700px', mx: 'auto' }}>
                  Explore high-potential sectors driving innovation, localization, and growth
                </Typography>
              </Box>
            </motion.div>

            <Grid container spacing={4}>
              {(featuredIndustries.length > 0 ? featuredIndustries : industries.slice(0, 3))
                .map((industry, index) => (
                  <Grid item xs={12} md={4} key={industry.id}>
                    <motion.div
                      initial={{ opacity: 0, y: 40 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                    >
                      <MotionCard
                        whileHover={{
                          y: -16,
                          scale: 1.03,
                          transition: { duration: 0.3 }
                        }}
                        elevation={0}
                      sx={{
                        borderRadius: 4,
                        height: '100%',
                        position: 'relative',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                        boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
                        '&:hover': {
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                          boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.2)}`
                        }
                      }}
                    >
                      <CardActionArea
                        onClick={() => handleIndustryClick(industry.id)}
                        sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                      >
                        {/* Icon Header with Enhanced Gradient */}
                        <Box
                          sx={{
                            height: 200,
                            background: `linear-gradient(135deg,
                              ${alpha(theme.palette.primary.light, 0.95)} 0%,
                              ${alpha(theme.palette.primary.main, 1)} 50%,
                              ${alpha(theme.palette.primary.dark, 1)} 100%)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: '-50%',
                              right: '-50%',
                              width: '200%',
                              height: '200%',
                              background: 'radial-gradient(circle, rgba(255,255,255,0.25) 0%, transparent 70%)',
                            },
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: '4px',
                              background: `linear-gradient(90deg,
                                ${alpha(theme.palette.secondary.main, 0.8)},
                                ${alpha(theme.palette.primary.light, 0.8)})`,
                            }
                          }}
                        >
                          <Box
                            className="industry-icon"
                            sx={{
                              bgcolor: 'white',
                              width: 110,
                              height: 110,
                              borderRadius: '24px',
                              boxShadow: '0 12px 24px rgba(0,0,0,0.2)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              position: 'relative',
                              zIndex: 1,
                              fontSize: '3.5rem',
                              color: theme.palette.primary.main,
                              transition: 'transform 0.3s ease',
                              '.MuiCard-root:hover &': {
                                transform: 'scale(1.1) rotate(5deg)'
                              }
                            }}
                          >
                            {getIndustryIcon(industry.icon)}
                          </Box>
                        </Box>

                        <CardContent sx={{ p: 3.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 700,
                              mb: 1.5,
                              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                              backgroundClip: 'text',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              fontSize: '1.5rem'
                            }}
                          >
                            {industry.name}
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              mb: 3,
                              flexGrow: 1,
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              color: theme.palette.text.secondary,
                              lineHeight: 1.7,
                              fontSize: '0.95rem'
                            }}
                          >
                            {industry.description || 'Explore this industry sector for comprehensive insights and opportunities'}
                          </Typography>

                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 'auto' }}>
                            <Chip
                              label="Investment Ready"
                              size="small"
                              icon={<Box component="span" sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: theme.palette.success.main,
                                ml: 1
                              }} />}
                              sx={{
                                borderRadius: 2,
                                bgcolor: alpha(theme.palette.success.main, 0.12),
                                color: theme.palette.success.dark,
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                border: `1.5px solid ${alpha(theme.palette.success.main, 0.4)}`,
                                px: 1.5,
                                py: 0.5,
                                height: 28,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.success.main, 0.2),
                                  transform: 'translateY(-2px)'
                                }
                              }}
                            />
                            <Chip
                              label="High Growth"
                              size="small"
                              icon={<Box component="span" sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: theme.palette.info.main,
                                ml: 1
                              }} />}
                              sx={{
                                borderRadius: 2,
                                bgcolor: alpha(theme.palette.info.main, 0.12),
                                color: theme.palette.info.dark,
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                border: `1.5px solid ${alpha(theme.palette.info.main, 0.4)}`,
                                px: 1.5,
                                py: 0.5,
                                height: 28,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.info.main, 0.2),
                                  transform: 'translateY(-2px)'
                                }
                              }}
                            />
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </MotionCard>
                    </motion.div>
                  </Grid>
                ))}
            </Grid>
          </Box>

          {/* Marketplace Section */}
          <Box sx={{ mb: 8 }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 5 }}>
                <Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 800,
                      mb: 1,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    Marketplace
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Connect with investors, discover innovation opportunities, and explore promising projects
                  </Typography>
                </Box>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate('/marketplace')}
                    sx={{
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: 2.5,
                      px: 3,
                      py: 1.5,
                      borderWidth: 2,
                      '&:hover': {
                        borderWidth: 2,
                        background: alpha(theme.palette.primary.main, 0.05)
                      }
                    }}
                  >
                    View All
                  </Button>
                </motion.div>
              </Box>
            </motion.div>

            {/* Enhanced Tabs */}
            <Box sx={{ mb: 4 }}>
              <Tabs
                value={marketplaceTab}
                onChange={handleMarketplaceTabChange}
                textColor="primary"
                indicatorColor="primary"
                sx={{
                  '& .MuiTab-root': {
                    minWidth: 'auto',
                    px: 4,
                    py: 1.5,
                    mr: 2,
                    borderRadius: 2.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    transition: 'all 0.3s ease',
                    border: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
                    '&.Mui-selected': {
                      background: alpha(theme.palette.primary.main, 0.1),
                      border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                      color: theme.palette.primary.main
                    },
                    '&:hover': {
                      background: alpha(theme.palette.primary.main, 0.05)
                    }
                  },
                  '& .MuiTabs-indicator': {
                    display: 'none'
                  }
                }}
              >
                <Tab label="Investment Opportunities" />
                <Tab label="Investor Requests" />
              </Tabs>
            </Box>

            {/* Investment Opportunities Tab Panel */}
            <AnimatePresence mode="wait">
              {marketplaceTab === 0 && (
                <motion.div
                  key="opportunities"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4 }}
                >
                  <Grid container spacing={3}>
                    {investmentOpportunities.slice(0, 3).map((opportunity, index) => (
                      <Grid item xs={12} md={4} key={opportunity.id}>
                        <motion.div
                          initial={{ opacity: 0, y: 30 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: index * 0.1 }}
                        >
                          <MotionCard
                            whileHover={{ y: -10, scale: 1.02 }}
                            elevation={0}
                            sx={{
                              borderRadius: 3,
                              height: '100%',
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                              boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.06)}`,
                              '&:hover': {
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.15)}`
                              }
                            }}
                          >
                      <CardActionArea
                        onClick={() => navigate(`/marketplace/opportunity/${opportunity.id}`)}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'stretch',
                          position: 'relative',
                          '&:hover .company-logo': {
                            transform: 'scale(1.05)',
                          }
                        }}
                      >
                        {/* Subtle top accent */}
                        <Box
                          sx={{
                            height: 4,
                            width: '100%',
                            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                          }}
                        />

                        <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                          {/* Logo and Title Section */}
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2.5, gap: 2 }}>
                            <Avatar
                              className="company-logo"
                              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(opportunity.organization)}&background=random&size=70&bold=true`}
                              alt={opportunity.organization}
                              sx={{
                                width: 70,
                                height: 70,
                                border: `3px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                                flexShrink: 0,
                                transition: 'transform 0.2s ease',
                                background: alpha(theme.palette.background.paper, 0.8)
                              }}
                            />
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 700,
                                  lineHeight: 1.3,
                                  mb: 0.5,
                                  color: theme.palette.text.primary,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                }}
                              >
                                {opportunity.projectName}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <BusinessIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    fontWeight: 500,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {opportunity.organization}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>

                          {/* Sector Badge */}
                          <Box
                            sx={{
                              mb: 2,
                              p: 1.5,
                              borderRadius: 1.5,
                              bgcolor: alpha(theme.palette.primary.main, 0.05),
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: theme.palette.primary.main,
                                fontSize: '0.875rem'
                              }}
                            >
                              {opportunity.sector}
                            </Typography>
                          </Box>

                          {/* Status Chips */}
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                            <Chip
                              label={opportunity.stage}
                              size="small"
                              color={
                                opportunity.stage === "Ready-to-Build" ? "success" :
                                  opportunity.stage === "Feasibility" ? "primary" : "secondary"
                              }
                              sx={{ borderRadius: 1 }}
                            />
                            <Chip
                              label={opportunity.country}
                              size="small"
                              variant="outlined"
                              sx={{ borderRadius: 1 }}
                            />
                          </Box>

                          {/* Summary */}
                          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                            {opportunity.summary?.substring(0, 100)}
                            {opportunity.summary?.length > 100 ? '...' : ''}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </MotionCard>
                        </motion.div>
                  </Grid>
                ))}
              </Grid>
                </motion.div>
            )}

            {/* Investor Requests Tab Panel */}
            {marketplaceTab === 1 && (
              <motion.div
                key="requests"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
              >
              <Grid container spacing={3}>
                {investorRequests.slice(0, 3).map((request, index) => (
                  <Grid item xs={12} md={4} key={request.id}>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                    >
                      <MotionCard
                        whileHover={{ y: -10, scale: 1.02 }}
                        elevation={0}
                        sx={{
                          borderRadius: 3,
                          height: '100%',
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.secondary.main, 0.02)})`,
                          border: `1px solid ${alpha(theme.palette.secondary.main, 0.15)}`,
                          boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.06)}`,
                          '&:hover': {
                            border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                            boxShadow: `0 12px 40px ${alpha(theme.palette.secondary.main, 0.15)}`
                          },
                          '&:hover .company-logo': {
                            transform: 'scale(1.05)'
                          }
                        }}
                      >
                      <CardActionArea
                        onClick={() => navigate(`/marketplace/request/${request.id}`)}
                        sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                      >
                        {/* Subtle top accent */}
                        <Box
                          sx={{
                            height: 4,
                            width: '100%',
                            background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.light})`,
                          }}
                        />

                        <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                          {/* Logo and Title Section */}
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2.5, gap: 2 }}>
                            <Avatar
                              className="company-logo"
                              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(request.organization)}&background=random&size=70&bold=true`}
                              alt={request.organization}
                              sx={{
                                width: 70,
                                height: 70,
                                border: `3px solid ${alpha(theme.palette.secondary.main, 0.15)}`,
                                boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.15)}`,
                                flexShrink: 0,
                                transition: 'transform 0.2s ease',
                                background: alpha(theme.palette.background.paper, 0.8)
                              }}
                            />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 600,
                                  mb: 0.5,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden'
                                }}
                              >
                                {request.projectName}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  fontWeight: 500
                                }}
                              >
                                <BusinessIcon fontSize="small" />
                                {request.organization}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Sector Badge */}
                          <Box
                            sx={{
                              mb: 2,
                              p: 1.5,
                              borderRadius: 1.5,
                              bgcolor: alpha(theme.palette.secondary.main, 0.05),
                              border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.secondary.main }}>
                              {request.sector}
                            </Typography>
                          </Box>

                          {/* Description */}
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mb: 2,
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              flexGrow: 1
                            }}
                          >
                            {request.goal}
                          </Typography>

                          {/* Type Chip */}
                          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 'auto' }}>
                            <Chip
                              label="Investor Request"
                              size="small"
                              color="secondary"
                              sx={{ borderRadius: 1, fontWeight: 500 }}
                            />
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </MotionCard>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
              </motion.div>
            )}
          </AnimatePresence>
          </Box>

          {/* Our Members Section */}
          <Box sx={{ mb: 8 }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Box sx={{ textAlign: 'center', mb: 5 }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    mb: 2,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  Our Members
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: theme.palette.text.secondary,
                    maxWidth: 700,
                    mx: 'auto',
                    fontWeight: 400
                  }}
                >
                  Join a diverse ecosystem of industry leaders, innovators, and strategic enablers
                </Typography>
              </Box>
            </motion.div>

            <Grid container spacing={3} justifyContent="center">
              {[
                {
                  name: 'Government',
                  icon: <BuildingIcon fontSize="large" />,
                  color: '#f9a825', // Yellow
                  gradient: 'linear-gradient(135deg, #f9a825, #fdd835)'
                },
                {
                  name: 'Investment Institution',
                  icon: <CurrencyIcon fontSize="large" />,
                  color: '#2e7d32', // Green
                  gradient: 'linear-gradient(135deg, #2e7d32, #66bb6a)'
                },
                {
                  name: 'Private Sector Company',
                  icon: <BusinessIcon fontSize="large" />,
                  color: '#ed6c02', // Orange
                  gradient: 'linear-gradient(135deg, #ed6c02, #ff9800)'
                },
                {
                  name: 'RDI',
                  icon: <ScienceIcon fontSize="large" />,
                  color: '#9c27b0', // Purple
                  gradient: 'linear-gradient(135deg, #9c27b0, #ba68c8)'
                },
                {
                  name: 'University',
                  icon: <TourIcon fontSize="large" />,
                  color: '#d32f2f', // Red
                  gradient: 'linear-gradient(135deg, #d32f2f, #ef5350)'
                },
                {
                  name: 'Startup',
                  icon: <TrendingUpIcon fontSize="large" />,
                  color: '#0288d1', // Cyan
                  gradient: 'linear-gradient(135deg, #0288d1, #29b6f6)'
                }
              ].map((segment, index) => {
                const count = members.filter(m => m.segment === segment.name).length;
                return (
                  <Grid item xs={6} sm={4} md={3} lg={2} key={segment.name}>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.08 }}
                    >
                      <MotionCard
                        whileHover={{ y: -12, scale: 1.05 }}
                        elevation={0}
                        sx={{
                          borderRadius: 3,
                          textAlign: 'center',
                          height: '100%',
                          minHeight: 180,
                          position: 'relative',
                          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(segment.color, 0.02)})`,
                          border: `1px solid ${alpha(segment.color, 0.15)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            border: `1px solid ${alpha(segment.color, 0.4)}`,
                            boxShadow: `0 20px 40px ${alpha(segment.color, 0.2)}`
                          }
                        }}
                      >
                        <CardActionArea
                          onClick={() => navigate(`/members?type=${segment.name}`)}
                          sx={{
                            py: 3.5,
                            px: 2,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {/* Glowing Icon Container */}
                          <Box sx={{ position: 'relative', display: 'inline-block', mb: 2.5 }}>
                            {/* Outer glow effect */}
                            <Box
                              sx={{
                                position: 'absolute',
                                inset: -12,
                                background: `radial-gradient(circle, ${alpha(segment.color, 0.4)}, transparent 70%)`,
                                filter: 'blur(20px)',
                                opacity: 0.8,
                                transition: 'all 0.3s ease',
                                '.MuiCard-root:hover &': {
                                  opacity: 1,
                                  inset: -16,
                                  filter: 'blur(24px)'
                                }
                              }}
                            />

                            {/* Icon circle */}
                            <Box
                              sx={{
                                position: 'relative',
                                p: 2.5,
                                borderRadius: '50%',
                                background: segment.gradient,
                                boxShadow: `0 8px 24px ${alpha(segment.color, 0.3)}`,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '.MuiCard-root:hover &': {
                                  transform: 'scale(1.1) rotateY(10deg)',
                                  boxShadow: `0 12px 32px ${alpha(segment.color, 0.4)}`
                                },
                                '& svg': {
                                  color: '#fff',
                                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                                }
                              }}
                            >
                              {segment.icon}

                              {/* Count badge */}
                              {count > 0 && (
                                <Chip
                                  label={count}
                                  size="small"
                                  sx={{
                                    position: 'absolute',
                                    top: -8,
                                    right: -8,
                                    minWidth: 28,
                                    height: 28,
                                    fontSize: '0.8rem',
                                    fontWeight: 700,
                                    background: alpha(theme.palette.background.paper, 0.95),
                                    color: segment.color,
                                    border: `2px solid ${segment.color}`,
                                    boxShadow: `0 4px 12px ${alpha(segment.color, 0.3)}`,
                                    backdropFilter: 'blur(10px)'
                                  }}
                                />
                              )}
                            </Box>
                          </Box>

                          {/* Member type name */}
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 700,
                              color: theme.palette.text.primary,
                              fontSize: '0.95rem',
                              lineHeight: 1.3,
                              minHeight: '2.6em',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.3s ease',
                              '.MuiCard-root:hover &': {
                                color: segment.color,
                                transform: 'translateY(-2px)'
                              }
                            }}
                          >
                            {segment.name}
                          </Typography>
                        </CardActionArea>
                      </MotionCard>
                    </motion.div>
                  </Grid>
                );
              })}
            </Grid>

            {/* View All Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/members')}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 2.5,
                    textTransform: 'none',
                    fontSize: '1.05rem',
                    fontWeight: 600,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.5)}`
                    }
                  }}
                >
                  View All Members
                </Button>
              </Box>
            </motion.div>
          </Box>

          {/* Latest Updates Section */}
          <Box sx={{ mb: 8 }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Box sx={{ textAlign: 'center', mb: 5 }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    mb: 2,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  Latest Updates
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: theme.palette.text.secondary,
                    maxWidth: 700,
                    mx: 'auto',
                    fontWeight: 400
                  }}
                >
                  Stay informed with the latest news, innovation signals, and expert insights
                </Typography>
              </Box>
            </motion.div>

            <Grid container spacing={4}>
              {/* News Section */}
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                >
                  <MotionCard
                    whileHover={{ y: -8 }}
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      height: '100%',
                      background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                      overflow: 'hidden',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                        boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.15)}`
                      }
                    }}
                  >
                    {/* Header with gradient */}
                    <Box
                      sx={{
                        p: 2.5,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                        borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent'
                        }}
                      >
                        Latest VCG News
                      </Typography>
                      <Button
                        size="small"
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => handleViewAllArticles('news')}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          color: theme.palette.primary.main,
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.1)
                          }
                        }}
                      >
                        View All
                      </Button>
                    </Box>

                    <CardContent sx={{ p: 0 }}>
                      {newsArticles.slice(0, 3).map((article, index) => (
                        <motion.div
                          key={article.id}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.3, delay: index * 0.1 }}
                        >
                          <Box
                            onClick={() => handleArticleClick('news', article.id)}
                            sx={{
                              p: 3,
                              position: 'relative',
                              '&:not(:last-child)': {
                                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                              },
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              cursor: 'pointer',
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: '4px',
                                background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                                transform: 'scaleY(0)',
                                transition: 'transform 0.3s ease'
                              },
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.05),
                                pl: 4,
                                '&::before': {
                                  transform: 'scaleY(1)'
                                }
                              }
                            }}
                          >
                            {/* Category badge */}
                            <Box sx={{ mb: 1.5 }}>
                              <Chip
                                label="News"
                                size="small"
                                sx={{
                                  height: 24,
                                  fontSize: '0.75rem',
                                  fontWeight: 700,
                                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.primary.light, 0.15)})`,
                                  color: theme.palette.primary.main,
                                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                  backdropFilter: 'blur(10px)',
                                  transition: 'all 0.3s ease',
                                  '.MuiBox-root:hover &': {
                                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.25)}, ${alpha(theme.palette.primary.light, 0.25)})`,
                                    transform: 'scale(1.05)'
                                  }
                                }}
                              />
                            </Box>

                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 700,
                                mb: 1.5,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                lineHeight: 1.5,
                                fontSize: '1.05rem',
                                transition: 'color 0.3s ease',
                                '.MuiBox-root:hover &': {
                                  color: theme.palette.primary.main
                                }
                              }}
                            >
                              {article.title}
                            </Typography>

                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 1.5
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.75,
                                  color: theme.palette.text.secondary
                                }}
                              >
                                <CalendarIcon sx={{ fontSize: '1rem' }} />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: '0.875rem'
                                  }}
                                >
                                  {article.date}
                                </Typography>
                              </Box>

                              {/* Read more indicator */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  color: theme.palette.primary.main,
                                  opacity: 0,
                                  transform: 'translateX(-10px)',
                                  transition: 'all 0.3s ease',
                                  '.MuiBox-root:hover &': {
                                    opacity: 1,
                                    transform: 'translateX(0)'
                                  }
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: '0.875rem'
                                  }}
                                >
                                  Read more
                                </Typography>
                                <ArrowForwardIcon sx={{ fontSize: '1rem' }} />
                              </Box>
                            </Box>
                          </Box>
                        </motion.div>
                      ))}
                    </CardContent>
                  </MotionCard>
                </motion.div>
              </Grid>

              {/* Innovation Section */}
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <MotionCard
                    whileHover={{ y: -8 }}
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      height: '100%',
                      background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.secondary.main, 0.02)})`,
                      border: `1px solid ${alpha(theme.palette.secondary.main, 0.15)}`,
                      overflow: 'hidden',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                        boxShadow: `0 20px 40px ${alpha(theme.palette.secondary.main, 0.15)}`
                      }
                    }}
                  >
                    {/* Header with gradient */}
                    <Box
                      sx={{
                        p: 2.5,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
                        borderBottom: `2px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent'
                        }}
                      >
                        Expert Insights
                      </Typography>
                      <Button
                        size="small"
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => handleViewAllArticles('innovation')}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          color: theme.palette.secondary.main,
                          '&:hover': {
                            bgcolor: alpha(theme.palette.secondary.main, 0.1)
                          }
                        }}
                      >
                        View All
                      </Button>
                    </Box>

                    <CardContent sx={{ p: 0 }}>
                      {innovationArticles.slice(0, 3).map((article, index) => (
                        <motion.div
                          key={article.id}
                          initial={{ opacity: 0, x: 20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.3, delay: index * 0.1 }}
                        >
                          <Box
                            onClick={() => handleArticleClick('innovation', article.id)}
                            sx={{
                              p: 3,
                              position: 'relative',
                              '&:not(:last-child)': {
                                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                              },
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              cursor: 'pointer',
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: '4px',
                                background: `linear-gradient(180deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.light})`,
                                transform: 'scaleY(0)',
                                transition: 'transform 0.3s ease'
                              },
                              '&:hover': {
                                bgcolor: alpha(theme.palette.secondary.main, 0.05),
                                pl: 4,
                                '&::before': {
                                  transform: 'scaleY(1)'
                                }
                              }
                            }}
                          >
                            {/* Category badge */}
                            <Box sx={{ mb: 1.5 }}>
                              <Chip
                                label="Insights"
                                size="small"
                                icon={<InsightsIcon sx={{ fontSize: '0.9rem !important', color: `${theme.palette.secondary.main} !important` }} />}
                                sx={{
                                  height: 24,
                                  fontSize: '0.75rem',
                                  fontWeight: 700,
                                  background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.15)}, ${alpha(theme.palette.secondary.light, 0.15)})`,
                                  color: theme.palette.secondary.main,
                                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                                  backdropFilter: 'blur(10px)',
                                  transition: 'all 0.3s ease',
                                  '.MuiBox-root:hover &': {
                                    background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.25)}, ${alpha(theme.palette.secondary.light, 0.25)})`,
                                    transform: 'scale(1.05)'
                                  }
                                }}
                              />
                            </Box>

                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 700,
                                mb: 1.5,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                lineHeight: 1.5,
                                fontSize: '1.05rem',
                                transition: 'color 0.3s ease',
                                '.MuiBox-root:hover &': {
                                  color: theme.palette.secondary.main
                                }
                              }}
                            >
                              {article.title}
                            </Typography>

                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 1.5
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.75,
                                  color: theme.palette.text.secondary
                                }}
                              >
                                <CalendarIcon sx={{ fontSize: '1rem' }} />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: '0.875rem'
                                  }}
                                >
                                  {article.date}
                                </Typography>
                              </Box>

                              {/* Read more indicator */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  color: theme.palette.secondary.main,
                                  opacity: 0,
                                  transform: 'translateX(-10px)',
                                  transition: 'all 0.3s ease',
                                  '.MuiBox-root:hover &': {
                                    opacity: 1,
                                    transform: 'translateX(0)'
                                  }
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: '0.875rem'
                                  }}
                                >
                                  Read more
                                </Typography>
                                <ArrowForwardIcon sx={{ fontSize: '1rem' }} />
                              </Box>
                            </Box>
                          </Box>
                        </motion.div>
                      ))}
                    </CardContent>
                  </MotionCard>
                </motion.div>
              </Grid>
            </Grid>
          </Box>

          {/* Experts Section for Home Page - Add this below the Updates Section */}
          <Box sx={{ mb: 8 }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Box sx={{ textAlign: 'center', mb: 5 }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    mb: 2,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  Featured Experts
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: theme.palette.text.secondary,
                    maxWidth: 700,
                    mx: 'auto',
                    fontWeight: 400
                  }}
                >
                  Connect with industry leaders, innovation experts, and domain specialists
                </Typography>
              </Box>
            </motion.div>

            <Grid container spacing={3}>
              {featuredExperts.map((expert, index) => (
                <Grid item xs={12} sm={6} md={4} key={expert.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <MotionCard
                      whileHover={{ y: -12, scale: 1.02 }}
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        height: '100%',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                          boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.15)}`
                        }
                      }}
                    >
                      <CardActionArea
                        onClick={() => navigate(`/expert/${expert.id}`)}
                        sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                      >
                        {/* Profile Photo Header with Gradient */}
                        <Box
                          sx={{
                            height: 200,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: `radial-gradient(circle at center, transparent 40%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`,
                              pointerEvents: 'none'
                            }
                          }}
                        >
                          {/* Glowing avatar effect */}
                          <Box sx={{ position: 'relative', display: 'inline-block' }}>
                            <Box
                              sx={{
                                position: 'absolute',
                                inset: -8,
                                background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.4)}, transparent 70%)`,
                                filter: 'blur(16px)',
                                opacity: 0.6,
                                transition: 'all 0.3s ease',
                                '.MuiCard-root:hover &': {
                                  opacity: 1,
                                  filter: 'blur(20px)'
                                }
                              }}
                            />
                            <Avatar
                              src={expert.photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(expert.name)}&background=random&size=150`}
                              alt={expert.name}
                              sx={{
                                position: 'relative',
                                width: 120,
                                height: 120,
                                border: `4px solid ${theme.palette.background.paper}`,
                                boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.15)}`,
                                transition: 'all 0.3s ease',
                                '.MuiCard-root:hover &': {
                                  transform: 'scale(1.05)',
                                  boxShadow: `0 12px 32px ${alpha(theme.palette.common.black, 0.2)}`
                                }
                              }}
                            />
                          </Box>
                        </Box>

                        <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                          {/* Name and Tag */}
                          <Box sx={{ textAlign: 'center', mb: 2.5 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                mb: 1,
                                transition: 'color 0.3s ease',
                                '.MuiCard-root:hover &': {
                                  color: theme.palette.primary.main
                                }
                              }}
                            >
                              {expert.name}
                            </Typography>
                            <Chip
                              label={expert.tag || "Expert"}
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.15)}, ${alpha(theme.palette.secondary.light, 0.15)})`,
                                color: theme.palette.secondary.main,
                                border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                                transition: 'all 0.3s ease',
                                '.MuiCard-root:hover &': {
                                  transform: 'scale(1.05)'
                                }
                              }}
                            />
                          </Box>

                          {/* Title and Organization */}
                          <Box
                            sx={{
                              textAlign: 'center',
                              mb: 2.5,
                              pb: 2.5,
                              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.text.secondary,
                                fontWeight: 600,
                                mb: 0.5,
                                fontSize: '0.95rem'
                              }}
                            >
                              {expert.title}
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                mt: 1
                              }}
                            >
                              <Avatar
                                src={expert.companyLogo || `https://ui-avatars.com/api/?name=${encodeURIComponent(expert.organization || 'Company')}&background=random&size=40&bold=true`}
                                alt={expert.organization || 'Company'}
                                variant="rounded"
                                sx={{
                                  width: 32,
                                  height: 32,
                                  border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                                  boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: theme.palette.text.primary
                                }}
                              >
                                {expert.organization}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Sectors */}
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'center', mt: 'auto' }}>
                            {expert.sectors.slice(0, 2).map((sector, idx) => (
                              <Chip
                                key={idx}
                                label={sector}
                                size="small"
                                sx={{
                                  height: 26,
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  background: alpha(theme.palette.primary.main, 0.08),
                                  color: theme.palette.primary.main,
                                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                  transition: 'all 0.3s ease',
                                  '.MuiCard-root:hover &': {
                                    background: alpha(theme.palette.primary.main, 0.15),
                                    transform: 'translateY(-2px)'
                                  }
                                }}
                              />
                            ))}
                            {expert.sectors.length > 2 && (
                              <Chip
                                label={`+${expert.sectors.length - 2}`}
                                size="small"
                                sx={{
                                  height: 26,
                                  fontSize: '0.75rem',
                                  fontWeight: 700,
                                  background: alpha(theme.palette.text.secondary, 0.08),
                                  color: theme.palette.text.secondary,
                                  border: `1px solid ${alpha(theme.palette.text.secondary, 0.2)}`,
                                  transition: 'all 0.3s ease',
                                  '.MuiCard-root:hover &': {
                                    background: alpha(theme.palette.text.secondary, 0.15),
                                    transform: 'translateY(-2px)'
                                  }
                                }}
                              />
                            )}
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </MotionCard>
                  </motion.div>
                </Grid>
              ))}
            </Grid>

            {/* View All Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/experts')}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 2.5,
                    textTransform: 'none',
                    fontSize: '1.05rem',
                    fontWeight: 600,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.5)}`
                    }
                  }}
                >
                  View All Experts
                </Button>
              </Box>
            </motion.div>
          </Box>

          {/* Call to Action Section - Hero Style */}
          <Box
            sx={{
              position: 'relative',
              minHeight: '300px',
              mb: 6,
              borderRadius: 3,
              overflow: 'hidden',
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.95)}, ${alpha(theme.palette.secondary.main, 0.90)})`,
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(135deg, transparent 0%, rgba(0,0,0,0.2) 100%)',
                pointerEvents: 'none'
              }
            }}
          >
            {/* Gradient Orbs - Same as hero */}
            <Box
              sx={{
                position: 'absolute',
                top: '10%',
                left: '5%',
                width: '250px',
                height: '250px',
                borderRadius: '50%',
                background: `radial-gradient(circle, ${alpha('#4FC3F7', 0.15)}, transparent 70%)`,
                filter: 'blur(60px)',
                animation: 'float1 20s ease-in-out infinite'
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: '15%',
                right: '10%',
                width: '200px',
                height: '200px',
                borderRadius: '50%',
                background: `radial-gradient(circle, ${alpha('#BA68C8', 0.15)}, transparent 70%)`,
                filter: 'blur(60px)',
                animation: 'float2 18s ease-in-out infinite'
              }}
            />

            {/* Content */}
            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 10, py: { xs: 5, md: 6 } }}>
              <Box sx={{ textAlign: 'center' }}>
                {/* Badge */}
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, type: 'spring', bounce: 0.5 }}
                >
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: -8,
                        background: `radial-gradient(circle, ${alpha('#00E5FF', 0.4)}, transparent 70%)`,
                        borderRadius: 10,
                        filter: 'blur(20px)',
                        animation: 'badgeGlow 3s ease-in-out infinite'
                      }}
                    />
                    <Chip
                      label="Start Your Journey"
                      icon={<TrendingUpIcon />}
                      sx={{
                        position: 'relative',
                        bgcolor: alpha('#00E5FF', 0.15),
                        color: '#00E5FF',
                        fontWeight: 800,
                        fontSize: '0.9rem',
                        px: 3,
                        py: 2,
                        height: 'auto',
                        mb: 3,
                        backdropFilter: 'blur(30px)',
                        border: `2px solid ${alpha('#00E5FF', 0.5)}`,
                        boxShadow: `0 0 40px ${alpha('#00E5FF', 0.4)}, inset 0 0 20px ${alpha('#00E5FF', 0.1)}`,
                        '& .MuiChip-icon': {
                          color: '#00E5FF',
                          fontSize: '1.1rem',
                          filter: 'drop-shadow(0 0 8px #00E5FF)'
                        }
                      }}
                    />
                  </Box>
                </motion.div>

                {/* Title */}
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: -20,
                        background: `radial-gradient(ellipse, ${alpha(theme.palette.primary.main, 0.3)}, transparent 70%)`,
                        filter: 'blur(40px)',
                        animation: 'titleAmbient 5s ease-in-out infinite'
                      }}
                    />
                    <Typography
                      variant="h2"
                      sx={{
                        position: 'relative',
                        background: `linear-gradient(135deg, #fff 0%, ${theme.palette.primary.light} 50%, ${theme.palette.secondary.light} 100%)`,
                        backgroundSize: '200% 200%',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontWeight: 900,
                        fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                        lineHeight: 1.2,
                        mb: 2,
                        filter: `drop-shadow(0 0 30px ${alpha(theme.palette.primary.main, 0.6)})`,
                        animation: 'titleGradient 8s ease-in-out infinite'
                      }}
                    >
                      Ready to Explore Innovation and Investment Opportunities?
                    </Typography>
                  </Box>
                </motion.div>

                {/* Description */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: alpha('#fff', 0.9),
                      fontWeight: 400,
                      lineHeight: 1.6,
                      mb: 4,
                      maxWidth: '700px',
                      mx: 'auto',
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      textShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.5)}`
                    }}
                  >
                    Discover comprehensive industry intelligence, identify where innovation fits, and unlock high-potential opportunities
                  </Typography>
                </motion.div>

                {/* Button */}
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.6, type: 'spring', bounce: 0.5 }}
                  whileHover={{ scale: 1.1, rotateY: 10 }}
                  style={{ perspective: '1000px' }}
                >
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: -15,
                        background: `radial-gradient(circle, ${alpha('#00E5FF', 0.6)}, transparent 70%)`,
                        borderRadius: 5,
                        filter: 'blur(25px)',
                        animation: 'buttonMegaGlow 2s ease-in-out infinite'
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: -8,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.4)}, ${alpha(theme.palette.secondary.main, 0.4)})`,
                        borderRadius: 4,
                        filter: 'blur(15px)'
                      }}
                    />
                    <Button
                      variant="contained"
                      size="large"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => navigate('/marketplace')}
                      sx={{
                        position: 'relative',
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        color: 'white',
                        px: 6,
                        py: 2,
                        fontSize: '1.1rem',
                        fontWeight: 800,
                        borderRadius: 3,
                        textTransform: 'none',
                        border: `2px solid ${alpha('#00E5FF', 0.5)}`,
                        boxShadow: `
                          0 0 40px ${alpha(theme.palette.primary.main, 0.8)},
                          inset 0 2px 10px ${alpha('#fff', 0.2)}
                        `,
                        '&:hover': {
                          background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.secondary.light})`,
                          transform: 'translateY(-6px)',
                          boxShadow: `
                            0 20px 60px ${alpha(theme.palette.primary.main, 0.9)},
                            0 0 60px ${alpha('#00E5FF', 0.8)}
                          `
                        },
                        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
                      }}
                    >
                      Get Started
                    </Button>
                  </Box>
                </motion.div>
              </Box>
            </Container>
          </Box>

        </Container>
      </MainContent>
    </Box>
  );
};

export default Home;


